import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import * as qs from 'qs';
import { LOGGER_LOG_TYPE, DEFAULT_LANGUAGE, API_BASE_URL, STORAGE } from '../../Config';
import { logout } from '../../store/authentication/action';
import store from '../../store/store';
import Logger from './Logger';
import Storage from './Storage';
import CustomFile from '../models/CustomFile';
import { UserToken } from 'api/account/models/UserToken';
import authService from './AuthorizeService';
import NavigationService from './NavigationService';
import Loading from './Loading';

const axiosConfig: AxiosRequestConfig = {
    responseType: 'json',
    // withCredentials: true,
};

const http = axios.create(axiosConfig);

class Http {
    private language: string = DEFAULT_LANGUAGE;

    constructor () {
        this.initInterceptors();
    }

    private initInterceptors() {
        // Request
        http.interceptors.request.use(async (config: AxiosRequestConfig) => {
            config.baseURL = API_BASE_URL;
            config.headers = await this.getHeaders();
            return config;
        }, (error: AxiosError) => error);

        // Response
        http.interceptors.response.use(async (response: AxiosResponse): Promise<any> => {
            return response.data;
        }, async (error: AxiosError) => {
            const originalRequest = error.config;

            if (error.response && error.response.status === 404) {
                NavigationService.goTo404();
                Loading.hide();
                return null
            }

            if (error.response && error.response.status === 401 && !(originalRequest as any)._retry) {
                (originalRequest as any)._retry = true; // Prevent looping
                try {
                    await authService.handleTokenRefresh();

                    return await http(originalRequest); // Retry the original request
                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError);
                    store.dispatch(logout() as any);
                }
            }

            return await Promise.reject(error);
        });
    }

    public setLanguage(language: string) {
        if (language) {
            this.language = language;
        }
    }

    public async post<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> {
        return await http.post<any, T>(url, data, config).catch((error: any) => this.onError(error, url));
    }

    public async postWithFile<T>(url: string, data: any, files: CustomFile[], config?: AxiosRequestConfig): Promise<T> {
        const formData = new FormData();

        for (const file of files) {
            formData.append(`${file.containerName ? file.containerName : 'files[]'}`, file);
        }

        formData.append('model', JSON.stringify(data));

        return await http.post<any, T>(url, formData, {
            ...(config ?? {}),
            headers: {
                'content-type': 'multipart/form-data',
            },
        }).catch((error: any) => this.onError(error, url));
    }

    public async postFormData<T>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<T> {
        return await http.post<any, T>(url, formData, {
            ...(config ?? {}),
            headers: {
                'content-type': 'multipart/form-data',
            },
        }).catch((error: any) => this.onError(error, url));
    }

    public async put<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> {
        return await http.put<any, T>(url, data, config).catch((error: any) => this.onError(error, url));
    }

    public async putWithFile<T>(url: string, data: any, files: CustomFile[], config?: AxiosRequestConfig): Promise<T> {
        const formData = new FormData();

        for (const file of files) {
            formData.append(`${file.containerName ? file.containerName : 'files[]'}`, file);
        }

        formData.append('model', JSON.stringify(data));

        return await http.put<any, T>(url, formData, {
            ...(config ?? {}),
            headers: {
                'content-type': 'multipart/form-data',
            },
        }).catch((error: any) => this.onError(error, url));
    }

    public async get<T>(url: string, params: any = null): Promise<T> {
        if (params) {
            url = `${url}?${qs.stringify(params, { allowDots: true })}`;
        }
        return await http.get<any, T>(url).catch((error: any) => this.onError(error, url));
    }

    public async getWithConfig<T>(url: string, params: any = null, config?: AxiosRequestConfig): Promise<T> {
        if (params) {
            url = `${url}?${qs.stringify(params)}`;
        }
        return await http.get<any, T>(url, config).catch((error: any) => this.onError(error, url));
    }

    public postWithConfig<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> {
        return http.post<any, T>(url, data, config).catch((error: any) => this.onError(error, url));
    }

    public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return await http.delete<any, T>(url, config).catch((error: any) => this.onError(error, url));
    }

    private readonly getHeaders = async () => {
        const token: UserToken = Storage.getObject(STORAGE.AUTH_USER_TOKEN);
        const language = Storage.get(STORAGE.CURRENT_LOCALE);
        return {
            Authorization: token ? `${token.tokenType} ${token.token}` : null,
            Accept: 'application/json, text/plain, */*',
            'Content-Type': 'application/json; charset=utf-8',
            'X-App-Lang': language
        };
    };

    private onError(error: any, url: string) {
        // Forward request errors to the backend, except failures of the /logger endpoint itself
        // (otherwise a logging failure would log itself, looping forever).
        const isLoggerEndpoint = (url ?? '').includes('logger');
        Logger.error(
            LOGGER_LOG_TYPE.REQUEST,
            error?.message ?? 'Request error',
            { url, status: error?.response?.status, data: error?.response?.data, error },
            !isLoggerEndpoint
        );
        return Promise.reject(error);
    }

    public createUrl(url: string, data?: any) {
        return `${API_BASE_URL}/${url}` + (data ? `?${qs.stringify(data)}` : '');
    }
}

export default new Http();
